// Two shared slots in the top bar that a route can fill from inside the
// <Outlet>: `route` (after the repo picker — the editor's changeset header) and
// `tab` (trailing — chrome belonging to the ACTIVE TAB, e.g. the review tab's
// view-mode selector and its accept/archive actions).
//
// Two slots rather than one because two owners fill them: the route owns the
// changeset header for as long as the changeset is open, while tab chrome comes
// and goes with the active tab. Portaling both into one node would order them by
// mount sequence, so the top bar would reshuffle itself as tabs switch.
//
// Mechanism: WorkspaceLayout provides a context holding the slot DOM nodes.
// TopBar mounts them via `useHeaderSlotRef`. A descendant renders
// <HeaderSlotPortal> to portal its content into one of them.
//
// Fallback: with no provider (e.g. a unit test that renders the route in
// isolation) the portal renders its children inline, so the controls remain
// present and testable.

import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";

export type HeaderSlot = "route" | "tab";

interface HeaderSlotValue {
  readonly nodes: Readonly<Record<HeaderSlot, HTMLElement | null>>;
  readonly setNode: (slot: HeaderSlot, n: HTMLElement | null) => void;
}

const HeaderSlotCtx = createContext<HeaderSlotValue | null>(null);

export function HeaderSlotProvider({ children }: { readonly children: ReactNode }) {
  const [nodes, setNodes] = useState<Record<HeaderSlot, HTMLElement | null>>({
    route: null,
    tab: null,
  });
  // `setNode` must keep a stable identity: `useHeaderSlotRef` hands it to a ref
  // callback, and a new function each render makes React detach (ref(null)) and
  // re-attach the node on every commit — which sets state, which re-renders.
  const setNode = useCallback<HeaderSlotValue["setNode"]>(
    (slot, n) => setNodes((prev) => (prev[slot] === n ? prev : { ...prev, [slot]: n })),
    [],
  );
  const value = useMemo<HeaderSlotValue>(() => ({ nodes, setNode }), [nodes, setNode]);
  return <HeaderSlotCtx.Provider value={value}>{children}</HeaderSlotCtx.Provider>;
}

/** Ref setter for one of the top-bar slot containers (used by TopBar). */
export function useHeaderSlotRef(slot: HeaderSlot = "route"): (n: HTMLElement | null) => void {
  const ctx = useContext(HeaderSlotCtx);
  const setNode = ctx?.setNode;
  return useMemo(() => (n: HTMLElement | null) => setNode?.(slot, n), [setNode, slot]);
}

/** Render `children` into a top-bar slot, or inline if no provider. */
export function HeaderSlotPortal({
  children,
  slot = "route",
}: {
  readonly children: ReactNode;
  readonly slot?: HeaderSlot;
}): ReactNode {
  const ctx = useContext(HeaderSlotCtx);
  if (!ctx) return <>{children}</>; // no provider — render in place
  const node = ctx.nodes[slot];
  if (!node) return null; // provider mounting — slot not ready yet
  return createPortal(children, node);
}
